import { action, computed, makeObservable } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { SEARCH_PAGE_SIZE } from '@/tracker/hooks/useServerSearch';
import { Board, BoardRef, CreateTaskRequest, Queue, Sprint, Task, TaskRef } from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

/** Создание задачи: справочники для формы и сам `POST /tasks` */
export class CreateIssueStore extends LocalStore {
  private readonly _alertsStore: AlertsStore;
  private readonly _queues = new ValueModel<Queue[]>([]);
  /** Выбранная очередь целиком: её типы задач и тип по умолчанию */
  private readonly _queue = new ValueModel<Queue | null>(null);
  /** Незакрытые спринты всех досок; для формы — только досок выбранной очереди */
  private readonly _openSprints = new ValueModel<Sprint[]>([]);
  private readonly _queueBoardIds = new ValueModel<string[]>([]);
  /** Доска из формы: её спринты тоже предлагаем, даже если задач очереди на ней ещё нет */
  private readonly _boardId = new ValueModel<string | null>(null);
  private readonly _loadingStage = new LoadingStageModel();
  private readonly _savingStage = new LoadingStageModel();
  /** Ответ по прежней очереди не должен перетереть выбранную позже */
  private _queueRequest = 0;

  constructor({ alertsStore }: { alertsStore: AlertsStore }) {
    super();
    this._alertsStore = alertsStore;

    makeObservable(this, {
      queues: computed,
      queue: computed,
      sprints: computed,
      isLoading: computed,
      isSaving: computed,
      load: action.bound,
      selectQueue: action.bound,
      selectBoard: action.bound,
      loadBoard: action.bound,
      searchBoards: action.bound,
      searchTasks: action.bound,
      create: action.bound,
    });
  }

  get queues() {
    return this._queues.value;
  }

  get queue() {
    return this._queue.value;
  }

  /** Спринты досок, на которых есть задачи выбранной очереди, и выбранной доски */
  get sprints() {
    const boardIds = [...this._queueBoardIds.value, this._boardId.value];

    return this._openSprints.value.filter((sprint) => boardIds.includes(sprint.board.id));
  }

  get isLoading() {
    return this._loadingStage.isLoading;
  }

  get isSaving() {
    return this._savingStage.isLoading;
  }

  async load() {
    try {
      this._loadingStage.loading();

      const [queues, sprints] = await Promise.all([
        trackerApi.searchAllQueues({ orderBy: [{ field: 'key' }] }),
        // `-` — спринты всех досок
        trackerApi.searchAllSprints({
          boardId: '-',
          request: { filters: { status: { in: ['active', 'draft'] } } },
        }),
      ]);

      this._queues.change(queues);
      this._openSprints.change(sprints);
      this._loadingStage.success();
    } catch (error) {
      this._loadingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить очереди', error });
    }
  }

  async selectQueue(key: string | null) {
    const request = ++this._queueRequest;

    this._queue.change(null);
    this._queueBoardIds.change([]);

    if (!key) return;

    try {
      const [queue, boards] = await Promise.all([
        trackerApi.getQueue(key),
        trackerApi.searchAllBoards({ filters: { queue: { eq: key } } }),
      ]);

      if (request !== this._queueRequest) return;

      this._queue.change(queue);
      this._queueBoardIds.change(boards.map((board) => board.id));
    } catch (error) {
      showError({
        alertsStore: this._alertsStore,
        title: `Не удалось загрузить очередь ${key}`,
        error,
      });
    }
  }

  selectBoard(boardId: string | null) {
    this._boardId.change(boardId);
  }

  /** Доска из ?board= — для подписи в поле. null — не нашлась (ошибка уже показана) */
  async loadBoard(boardId: string): Promise<BoardRef | null> {
    try {
      const { id, name } = await trackerApi.getBoard(boardId);

      return { id, name };
    } catch (error) {
      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить доску', error });

      return null;
    }
  }

  /** Доски — подсказки сервера: их сотни, мои первыми */
  async searchBoards(query: string): Promise<Board[]> {
    try {
      return await trackerApi.suggestBoards({
        query: query.trim() || undefined,
        pageSize: SEARCH_PAGE_SIZE,
      });
    } catch {
      return [];
    }
  }

  /** Кандидаты в родители — подсказки сервера; без ввода — мои недавние задачи */
  async searchTasks(query: string): Promise<TaskRef[]> {
    try {
      return await trackerApi.suggestTasks({
        query: query.trim() || undefined,
        pageSize: SEARCH_PAGE_SIZE,
      });
    } catch {
      return [];
    }
  }

  /**
   * Созданная задача или null, если не вышло (ошибка уже показана).
   * Ни доски, ни спринта в POST /tasks нет — задача добавляется в них следующими запросами
   */
  async create({
    data,
    board,
    sprint,
  }: {
    data: CreateTaskRequest;
    board: BoardRef | null;
    sprint: Sprint | null;
  }) {
    let task: Task;

    try {
      this._savingStage.loading();
      task = await trackerApi.createTask(data);
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось создать задачу', error });

      return null;
    }

    const keys = [task.key];

    if (board) {
      await this.attach({
        title: `Задача ${task.key} создана, но не добавлена на доску`,
        run: () => trackerApi.addTasksToBoard({ boardId: board.id, keys }),
      });
    }

    if (sprint) {
      await this.attach({
        title: `Задача ${task.key} создана, но не добавлена в спринт`,
        run: () =>
          trackerApi.addTasksToSprint({ boardId: sprint.board.id, sprintId: sprint.id, keys }),
      });
    }

    this._savingStage.success();

    return task;
  }

  /** Задача уже создана: ошибку показываем, но не прерываемся */
  private async attach({ title, run }: { title: string; run: () => Promise<unknown> }) {
    try {
      await run();
    } catch (error) {
      showError({ alertsStore: this._alertsStore, title, error });
    }
  }
}
